import RequestSortType from '@/js/enums/RequestSortType';

export const COLUMNS = [
    // № нельзя ни выключить (required), ни переставить (orderLocked): закрепление
    // отсчитывается с начала строки, и первая колонка — опора всей цепочки.
    // Закрепление при этом обычная настройка пользователя, по умолчанию выключенная.
    {
        key: 'id',
        title: '№',
        width: 64,
        sortType: RequestSortType.ID,
        required: true,
        orderLocked: true,
        defaultVisible: true,
    },
    // Заголовок здесь — дефолт/фолбэк для мест, не знающих о настройке источника текста
    // (например, реестра COLUMNS как такового). Реально показанный заголовок колонки
    // «Описание»/«Последнее сообщение» переопределяется в RequestStore.vue по значению
    // account-settings-ключа request-store.text-column-source.
    { key: 'description', title: 'Описание', width: null, sortType: null, required: false, defaultVisible: true },
    {
        // 136px = 92px самой длинной метки «Приостановлено» (11px Factor B Bold) + 20px
        // паддинга метки + 20px паддинга ячейки. Под Roboto хватало 130px.
        key: 'status',
        title: 'Статус',
        width: 136,
        sortType: RequestSortType.STATUS,
        required: false,
        defaultVisible: true,
    },
    {
        // Метка P1–P4 узкая, ширину задаёт заголовок: 75px «ПРИОРИТЕТ» (11px Factor B Bold
        // с разрядкой) + 13px стрелки сортировки + 20px отступов ячейки.
        key: 'priority',
        title: 'Приоритет',
        width: 108,
        sortType: RequestSortType.PRIORITY,
        required: false,
        defaultVisible: true,
    },
    {
        key: 'author',
        title: 'Автор',
        width: 180,
        sortType: RequestSortType.AUTHOR,
        required: false,
        defaultVisible: true,
    },
    {
        key: 'executor',
        title: 'Исполнитель',
        width: 190,
        sortType: RequestSortType.EXECUTOR,
        required: false,
        defaultVisible: true,
    },
    {
        // 244px = 212px самой длинной строки («возобновится через 22 минуты», 14px Factor B
        // Bold — подсвеченный срок набран жирным) + 30px отступов ячейки (колонка по
        // умолчанию последняя, у последней слева 20px). Срок пишется относительным
        // временем, и обрезать его многоточием нечитаемо. В других размерах текста
        // ширина пересчитывается в requestRowData.scaledLength.
        key: 'deadline',
        title: 'Срок',
        width: 244,
        sortType: RequestSortType.DEADLINE,
        required: false,
        defaultVisible: true,
    },
    {
        key: 'created',
        title: 'Дата создания',
        width: 140,
        sortType: RequestSortType.DATE,
        required: false,
        defaultVisible: false,
    },
    {
        key: 'service',
        title: 'Услуга',
        width: 160,
        sortType: RequestSortType.SERVICE,
        required: false,
        defaultVisible: false,
    },
    {
        key: 'category',
        title: 'Категория',
        width: 160,
        sortType: RequestSortType.CATEGORY,
        required: false,
        defaultVisible: false,
    },
    { key: 'city', title: 'Город', width: 130, sortType: RequestSortType.CITY, required: false, defaultVisible: false },
    {
        key: 'group',
        title: 'Группа',
        width: 160,
        sortType: RequestSortType.GROUP,
        required: false,
        defaultVisible: false,
    },
    {
        key: 'activity',
        title: 'Активность',
        width: 140,
        sortType: RequestSortType.ACTIVITY,
        required: false,
        defaultVisible: false,
    },
    // 136px: заголовок «НЕПРОЧИТАННЫЕ» — 113px в 11px Factor B Bold с разрядкой.
    { key: 'unread', title: 'Непрочитанные', width: 136, sortType: null, required: false, defaultVisible: false },
];

const byKey = new Map(COLUMNS.map((column) => [column.key, column]));

// Замок на позицию, а не на закрепление: № всегда идёт первым, потому что цепочка
// закреплённых колонок отсчитывается от начала строки и ей нужна неподвижная опора.
// Само закрепление у № такая же настройка пользователя, как у остальных колонок.
export function isOrderLocked(key) {
    const column = byKey.get(key);

    return Boolean(column && column.orderLocked);
}

export function isRequired(key) {
    const column = byKey.get(key);

    return Boolean(column && column.required);
}

// Закреплённые колонки обязаны идти первыми: закрепление при горизонтальной прокрутке
// отсчитывает смещения от начала строки, и колонка после незакреплённой оказалась бы
// приклеена не к краю. Сохранённые конфиги приходят из /user-settings и порядка не
// гарантируют, а перетаскивание заголовков может уронить колонку выше закреплённой.
//
// № держится перед закреплёнными в любом случае: его позиция заблокирована, и всплывшая
// закреплённая колонка не вправе его обойти — иначе он уезжал бы при прокрутке.
export function pinnedFirst(config) {
    const anchor = config.filter((item) => isOrderLocked(item.key));
    const rest = config.filter((item) => !isOrderLocked(item.key));
    const pinned = rest.filter((item) => item.pinned);

    if (!anchor.length && !pinned.length) return config;

    return [...anchor, ...pinned, ...rest.filter((item) => !item.pinned)];
}

// Цепочка закреплённых начинается с первой колонки: lastStickyIndex обрывается на первой
// же незакреплённой, поэтому закрепление чего угодно втягивает в цепочку и № — иначе оно
// просто не сработало бы. Пины внутри цепочки разрывов не дают: pinnedFirst собирает их
// подряд сразу за №.
function withPinChain(config) {
    if (!config.length || config[0].pinned || !config.some((item) => item.pinned)) return config;

    return config.map((item, index) => (index === 0 ? { ...item, pinned: true } : item));
}

export function defaultConfig() {
    return COLUMNS.map((column) => ({
        key: column.key,
        visible: column.defaultVisible,
        pinned: false,
    }));
}

export function normalizeConfig(raw) {
    let saved;
    try {
        saved = Array.isArray(raw) ? raw : JSON.parse(raw);
    } catch (e) {
        return defaultConfig();
    }
    if (!Array.isArray(saved)) return defaultConfig();

    const seen = new Set();
    const config = [];

    for (const item of saved) {
        const column = item && byKey.get(item.key);
        if (!column || seen.has(column.key)) continue;
        seen.add(column.key);
        config.push({
            key: column.key,
            visible: column.required ? true : Boolean(item.visible),
            pinned: Boolean(item.pinned),
        });
    }
    for (const column of COLUMNS) {
        if (!seen.has(column.key)) {
            config.push({ key: column.key, visible: column.defaultVisible, pinned: false });
        }
    }

    return withPinChain(pinnedFirst(config));
}

export function serializeConfig(config) {
    return JSON.stringify(config.map(({ key, visible, pinned }) => ({ key, visible, pinned: Boolean(pinned) })));
}

// Колонка отдаётся вместе с настройкой закрепления: шапка, строки и расчёт смещений
// читают pinned прямо с объекта колонки и о конфиге ничего не знают.
export function visibleColumns(config) {
    return config.filter((item) => item.visible).map((item) => ({ ...byKey.get(item.key), pinned: item.pinned }));
}
